import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import * as yup from 'yup';

import { isNumber, isString } from '@utils/typeGuard';
import { validationMessage } from '@utils/validation';

type Options = {
  allowNullable?: boolean;
};

yup.addMethod(
  yup.object,
  'isDayjsValid',
  function method(message: string, options: Options) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return this.test('isDayjsValid', message, function validate(value: any) {
      if (options?.allowNullable && !value) {
        return true;
      }
      try {
        if (value && dayjs.isDayjs(value)) {
          if (dayjs(value).isValid()) {
            return true;
          }
        }
        return false;
      } catch {
        return false;
      }
    });
  },
);

yup.addMethod(
  yup.object,
  'dynamicMinDate',
  function method(message: string, minDate: Dayjs) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return this.test('dynamicMinDate', message, function validate(value: any) {
      if (!minDate || !value) {
        return true;
      }
      try {
        if (value && dayjs.isDayjs(value) && dayjs.isDayjs(minDate)) {
          if (dayjs(minDate).isBefore(value)) {
            return true;
          }
        }
        return false;
      } catch {
        return false;
      }
    });
  },
);

yup.addMethod(yup.string, 'isNumber', function method() {
  return this.test('isNumber', validationMessage.number, value => {
    if (isString(value)) return +value >= 0;
    return false;
  });
});

yup.addMethod(yup.string, 'onlyRussianLetters', function method() {
  return this.test(
    'onlyRussianLetters',
    validationMessage.onlyRusLetters,
    value => {
      if (value && value.length > 0) {
        return /^[А-Яа-яЁё]+$/.test(value);
      }
      return false;
    },
  );
});

yup.addMethod(yup.string, 'onlyRussianAndSpecialLetters', function method() {
  return this.test(
    'onlyRussianAndSpecialLetters',
    validationMessage.onlyRussianAndSpecialLetters,
    value => {
      if (value && value.length > 0) {
        // Важно соблюдать единообразие с бэком (используем одинаковую регулярку)
        return /^[а-яё0-9А-ЯЁT :;!?,."()-/№<>«»\n]+$/.test(value);
      }
      return false;
    },
  );
});

yup.addMethod(
  yup.number,
  'maxNumberLength',
  function method(integerLength: number, fractionalLength?: number) {
    return this.test(
      'maxNumberLength',
      validationMessage.maxLength(integerLength),
      value => {
        if (isNumber(value)) {
          const stringValue = value.toString();
          if (fractionalLength) {
            const regex = new RegExp(
              `^\\d{1,${integerLength}}(\\.\\d{1,${fractionalLength}})?$`,
            );
            return regex.test(stringValue);
          }
          return stringValue.length <= integerLength;
        }

        return false;
      },
    );
  },
);

export default yup;
